import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import logger from '../../../services/logger';

import {
  IntakeFormAgeRangeDTO,
  IntakeFormCareerConstraintsDTO,
  IntakeFormCareerPlanDTO,
  IntakeFormEducationDTO,
  IntakeFormLanguageProficiencyDTO,
  IntakeFormWorkHistoryDTO,
  OrganizationDTO,
  PersonalInfoDTO,
} from './form-intake.types';

export type IntakeFormState = Partial<
  IntakeFormAgeRangeDTO &
    PersonalInfoDTO &
    IntakeFormEducationDTO &
    IntakeFormLanguageProficiencyDTO &
    IntakeFormWorkHistoryDTO &
    IntakeFormCareerPlanDTO &
    IntakeFormCareerConstraintsDTO &
    OrganizationDTO
>;

const initialState: IntakeFormState = {
  name: '',
  addressLine1: '',
  city: '',
  state: '',
  phoneCountryCode: undefined,
  phone: '',
  email: '',
  ageRange: undefined,
  education: undefined,
  workStatus: [],
  workStatusComments: undefined,
  languageProficiency: {
    English: undefined,
    Spanish: undefined,
  },
  workHistory: undefined,
  careerPlan: undefined,
  careerPlanDirection: undefined,
  careerConstraints: [],
  careerConstraintComments: undefined,
  organization: undefined,
};

const intakeFormSlice = createSlice({
  name: 'formIntake',
  initialState,
  reducers: {
    updateAll: (state, action: PayloadAction<IntakeFormState>) => {
      logger.debug('Updating intake form state: all data.', { state, action });
      Object.assign(state, { ...action.payload });
    },
    updateAgeRange: (state, action: PayloadAction<IntakeFormAgeRangeDTO>) => {
      logger.debug('Updating intake form state: ageRange.', { state, action });
      state.ageRange = action.payload.ageRange;
    },
    updatePersonalInfo: (state, action: PayloadAction<PersonalInfoDTO>) => {
      logger.debug('Updating intake form state: personal information.', {
        state,
        action,
      });
      Object.assign(state, { ...action.payload });
    },
    updateEducation: (state, action: PayloadAction<IntakeFormEducationDTO>) => {
      logger.debug('Updating intake form state: education and work status.', {
        state,
        action,
      });
      Object.assign(state, { ...action.payload });
    },
    updateLanguageProficiency: (state, action: PayloadAction<IntakeFormLanguageProficiencyDTO>) => {
      logger.debug('Updating intake form state: language proficiency.', {
        state,
        action,
      });
      state.languageProficiency = action.payload.languageProficiency;
    },
    updateWorkHistory: (state, action: PayloadAction<IntakeFormWorkHistoryDTO>) => {
      logger.debug('Updating intake form state: work history.', {
        state,
        action,
      });
      state.workHistory = action.payload.workHistory;
    },
    updateCareerPlan: (state, action: PayloadAction<IntakeFormCareerPlanDTO>) => {
      logger.debug('Updating intake form state: career plan.', {
        state,
        action,
      });
      state.careerPlan = action.payload.careerPlan;
      state.careerPlanDirection = action.payload.careerPlanDirection;
    },
    updateCareerConstraints: (state, action: PayloadAction<IntakeFormCareerConstraintsDTO>) => {
      logger.debug('Updating intake form state: career constraints.', {
        state,
        action,
      });
      state.careerConstraints = action.payload.careerConstraints;
      state.careerConstraintComments = action.payload.careerConstraintComments;
    },
    updateOrganization: (state, action: PayloadAction<OrganizationDTO>) => {
      logger.debug('Updating intake form state: organization.', {
        state,
        action,
      });
      Object.assign(state, { ...action.payload });
    },
  },
});

export const {
  updateAll: updateAllAction,
  updateAgeRange: updateAgeRangeAction,
  updatePersonalInfo: updatePersonalInfoAction,
  updateEducation: updateEducationAction,
  updateLanguageProficiency: updateLanguageProficiencyAction,
  updateWorkHistory: updateWorkHistoryAction,
  updateCareerPlan: updateCareerPlanAction,
  updateCareerConstraints: updateCareerConstraintsAction,
  updateOrganization: updateOrganizationAction,
} = intakeFormSlice.actions;
export default intakeFormSlice.reducer;
